import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DirectionsCar as CarIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Driver, FleetVehicle } from '../../types';
import { driverAPI, vehicleAPI } from '../../services/api';

import AddDriverDialog from '../../components/AddDriverDialog';
import DriverAnalyticsDialog from '../../components/DriverAnalyticsDialog';
import AssignVehicleDialog from '../../components/AssignVehicleDialog';

const DriversPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [filteredDrivers, setFilteredDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<FleetVehicle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string>('');

  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openAnalyticsDialog, setOpenAnalyticsDialog] = useState<boolean>(false);
  const [openAssignDialog, setOpenAssignDialog] = useState<boolean>(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [driverToDelete, setDriverToDelete] = useState<Driver | null>(null);


  const loadDrivers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await driverAPI.getAllDrivers();
      setDrivers(data);
      setFilteredDrivers(data);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load drivers');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const data = await vehicleAPI.getAllVehicles({ limit: 100 });
      console.log('=== LOADED VEHICLES DEBUG ===');
      console.log('Total vehicles:', data.length);
      console.log('First vehicle:', data[0]);
      console.log('Vehicle IDs:', data.map(v => ({
        id: v.id,
        plateNumber: v.plateNumber
      })));
      setVehicles(data);
    } catch (e: any) {
      console.error('Failed to load vehicles:', e);
    }
  };




  useEffect(() => {
    loadDrivers();
    loadVehicles();
  }, []);

  useEffect(() => {
    let filtered = [...drivers];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(d =>
        d.firstName?.toLowerCase().includes(searchLower) ||
        d.lastName?.toLowerCase().includes(searchLower) ||
        d.email?.toLowerCase().includes(searchLower) ||
        d.licenseNumber?.toLowerCase().includes(searchLower) ||
        d.employeeId?.toLowerCase().includes(searchLower)
      );
    }

    if (status) {
      filtered = filtered.filter(d => d.status === status);
    }

    setFilteredDrivers(filtered);
  }, [search, status, drivers]);



  const handleAddDriver = async (driver: Driver) => {
    await loadDrivers();
    setOpenAddDialog(false);
  };

  const handleEditDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setOpenAddDialog(true);
  };

  const handleDeleteClick = (driver: Driver) => {
    setDriverToDelete(driver);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!driverToDelete) return;

    try {
      await driverAPI.deleteDriver(driverToDelete._id);
      await loadDrivers();
      setDeleteConfirmOpen(false);
      setDriverToDelete(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete driver');
    }
  };

  const handleViewAnalytics = (driver: Driver) => {
    setSelectedDriver(driver);
    setOpenAnalyticsDialog(true);
  };

  const handleAssignVehicle = (driver: Driver) => {
    setSelectedDriver(driver);
    setOpenAssignDialog(true);
  };

  const handleVehicleAssigned = async () => {
    await loadDrivers();
    setOpenAssignDialog(false);
  };

  const activeDrivers = drivers.filter(d => d.status === 'active').length;
  const assignedDrivers = drivers.filter(d => d.assignedVehicles).length;

  const getSafetyScoreColor = (score: number) => {
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      case 'on_leave': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3 }}>Driver Management</Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                placeholder="Search by name, email, license or employee ID"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="on_leave">On Leave</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={!isMobile && <RefreshIcon />}
                onClick={loadDrivers}
                size={isMobile ? 'small' : 'medium'}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={!isMobile && <AddIcon />}
                onClick={() => {
                  setSelectedDriver(null);
                  setOpenAddDialog(true);
                }}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'Add' : 'Add Driver'}
              </Button>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Chip label={`Total (${drivers.length})`} />
            <Chip color="success" label={`Active (${activeDrivers})`} />
            <Chip color="primary" label={`Assigned (${assignedDrivers})`} />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : filteredDrivers.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary">
                No drivers found. {search || status ? 'Try adjusting your filters.' : 'Add your first driver to get started!'}
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile Card View
            <Box>
              {filteredDrivers.map((driver) => (
                <Card key={driver._id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar src={driver.photo} sx={{ width: 48, height: 48 }}>
                          {driver.firstName[0]}{driver.lastName[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {driver.firstName} {driver.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {driver.employeeId || '-'}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        size="small"
                        label={driver.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(driver.status) as any}
                      />
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{driver.email}</Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{driver.phone}</Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">License</Typography>
                        <Typography variant="body2">{driver.licenseNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">Class {driver.licenseClass}</Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Safety Score</Typography>
                        <Box>
                          <Chip
                            size="small"
                            label={driver.safetyScore}
                            color={getSafetyScoreColor(driver.safetyScore) as any}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Assigned Vehicles</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          {driver.assignedVehicles && driver.assignedVehicles.length > 0 ? (
                            <Chip
                              size="small"
                              label={`${driver.assignedVehicles.length} Vehicle${driver.assignedVehicles.length > 1 ? 's' : ''}`}
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">None assigned</Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1.5 }} />

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <IconButton size="small" onClick={() => handleEditDriver(driver)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(driver)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>License</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Assigned Vehicle</TableCell>
                    <TableCell>Safety Score</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDrivers.map((driver) => (
                    <TableRow key={driver._id} hover>
                      {/* 1. Driver Info */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar src={driver.photo} sx={{ width: 40, height: 40 }}>
                            {driver.firstName[0]}{driver.lastName[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {driver.firstName} {driver.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {driver.employeeId || '-'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* 2. Contact */}
                      <TableCell>
                        <Typography variant="body2">{driver.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {driver.phone}
                        </Typography>
                      </TableCell>

                      {/* 3. License */}
                      <TableCell>
                        <Typography variant="body2">{driver.licenseNumber}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Class {driver.licenseClass}
                        </Typography>
                      </TableCell>

                      {/* 4. Status */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={driver.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(driver.status) as any}
                        />
                      </TableCell>

                      {/* 5. Assigned Vehicles */}
                      <TableCell>
                        {driver.assignedVehicles && driver.assignedVehicles.length > 0 ? (
                          <Box>
                            <Chip
                              size="small"
                              label={`${driver.assignedVehicles.length} Vehicle${driver.assignedVehicles.length > 1 ? 's' : ''}`}
                              color="primary"
                              variant="outlined"
                            />
                            <Box mt={1}>
                              <Button
                                size="small"
                                startIcon={<CarIcon />}
                                onClick={() => handleAssignVehicle(driver)}
                              >
                                Manage
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<CarIcon />}
                            onClick={() => handleAssignVehicle(driver)}
                            variant="contained"
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell>

                      {/* 6. Safety Score */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={driver.safetyScore}
                          color={getSafetyScoreColor(driver.safetyScore) as any}
                        />
                      </TableCell>

                      {/* 9. Actions */}
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditDriver(driver)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(driver)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <AddDriverDialog
        open={openAddDialog}
        driver={selectedDriver}
        onClose={() => {
          setOpenAddDialog(false);
          setSelectedDriver(null);
        }}
        onSaved={handleAddDriver}
      />

      <DriverAnalyticsDialog
        open={openAnalyticsDialog}
        driver={selectedDriver}
        onClose={() => {
          setOpenAnalyticsDialog(false);
          setSelectedDriver(null);
        }}
      />

      <AssignVehicleDialog
        open={openAssignDialog}
        driver={selectedDriver}
        vehicles={vehicles}
        onClose={() => {
          setOpenAssignDialog(false);
          setSelectedDriver(null);
        }}
        onAssigned={handleVehicleAssigned}
      />

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete driver {driverToDelete?.firstName} {driverToDelete?.lastName}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriversPage;
